import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Coffee, Salad, Utensils, Cookie, Coffee as Beverage } from 'lucide-react';
import { foodProfiles } from '../data/foodProfiles';

const RecipeCard = ({ recipe }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Breakfast':
        return Coffee;
      case 'Lunch':
        return Salad;
      case 'Dinner':
        return Utensils;
      case 'Snack':
        return Cookie;
      case 'Beverage':
        return Beverage;
      default:
        return Utensils;
    }
  };

  const Icon = getTypeIcon(recipe.type);

  // Get related food profile names
  const getRelatedFoodNames = () => {
    return recipe.relatedFoods
      .map(foodId => {
        const food = foodProfiles.find(f => f.id === foodId);
        return food ? { id: foodId, name: food.name } : null;
      })
      .filter(Boolean);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all">
      {/* Header - Always Visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-6 flex items-center justify-between text-left hover:bg-slate-50 transition-colors rounded-xl"
      >
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Icon className="h-5 w-5 text-blue-600" />
            </div>
            <span className="text-sm font-medium text-blue-600">
              {recipe.type}
            </span>
          </div>
          <h3 className="text-xl font-semibold text-slate-900">
            {recipe.name}
          </h3>
        </div>
        <div className="ml-4">
          {isExpanded ? (
            <ChevronUp className="h-6 w-6 text-slate-400" />
          ) : (
            <ChevronDown className="h-6 w-6 text-slate-400" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-6 pb-6 space-y-6 border-t border-slate-100 pt-6">
          {/* Why People Choose This Recipe */}
          <div>
            <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-3">
              Why People Choose This Recipe
            </h4>
            <p className="text-slate-700 leading-relaxed">
              {recipe.whyChosen}
            </p>
          </div>

          {/* Ingredients */}
          <div>
            <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-3">
              Ingredients
            </h4>
            <ul className="space-y-2">
              {recipe.ingredients.map((ingredient, index) => (
                <li
                  key={index}
                  className="flex items-start text-slate-700"
                >
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span>{ingredient}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Preparation Steps */}
          <div>
            <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-3">
              Preparation
            </h4>
            <ol className="space-y-3">
              {recipe.preparation.map((step, index) => (
                <li
                  key={index}
                  className="flex items-start text-slate-700"
                >
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium mr-3">
                    {index + 1}
                  </span>
                  <span className="flex-1">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Preparation Notes */}
          {recipe.preparationNotes && (
            <div>
              <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-3">
                Preparation Notes
              </h4>
              <p className="text-slate-700 leading-relaxed">
                {recipe.preparationNotes}
              </p>
            </div>
          )}

          {/* Related Food Profiles */}
          <div>
            <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-3">
              Related Food Profiles
            </h4>
            <div className="flex flex-wrap gap-2">
              {getRelatedFoodNames().map((food) => (
                <Link
                  key={food.id}
                  to="/integrative-health/food-nutrition"
                  className="inline-flex items-center px-3 py-1.5 bg-slate-50 hover:bg-blue-50 text-slate-700 hover:text-blue-700 text-sm rounded-full transition-colors border border-slate-200 hover:border-blue-200"
                >
                  {food.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Disclaimer */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-xs text-slate-700 leading-relaxed">
              This recipe is provided for general educational purposes only and is not intended as medical advice.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecipeCard;
